import { PropsWithChildren, RefObject, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';

import { Button, LoadingButton, SaveBar } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';
import { useIsVisible } from '~src/lib/hooks/useIsVisible';

type Props = PropsWithChildren & {
  viewportRef: RefObject<HTMLElement>;
};

interface ContextType {
  viewportRef?: Props['viewportRef'];
}

const Context = createContext<ContextType>({
  viewportRef: undefined,
});

function MutationSaveBarElement({ children, viewportRef }: Props) {
  const shouldHideSaveBar = useIsVisible(viewportRef, true);
  const { t } = useTranslation();

  return (
    <Context.Provider value={{ viewportRef }}>
      <SaveBar.Root open={!shouldHideSaveBar}>
        <SaveBar.Title>{t('common.edit.saveChanges')}?</SaveBar.Title>

        <SaveBar.Actions>{children}</SaveBar.Actions>
      </SaveBar.Root>
    </Context.Provider>
  );
}

function Cancel({ to, onClick }: { to: string; onClick?: () => void }) {
  const { t } = useTranslation();

  return (
    <SaveBar.Button>
      <Button variant="secondary" as={Link} to={to} onClick={onClick}>
        {t('common.cancel')}
      </Button>
    </SaveBar.Button>
  );
}

function Submit({
  label,
  onClick,
  isLoading = false,
}: {
  isLoading?: boolean;
  label?: string;
  onClick: () => Promise<void>;
}) {
  const { viewportRef } = useContext(Context);

  async function handleClick() {
    try {
      await onClick();
    } catch (e) {
      viewportRef?.current?.scrollIntoView({ behavior: 'smooth' });
      throw e;
    }
  }

  return (
    <LoadingButton loading={isLoading} onClick={handleClick}>
      {label}
    </LoadingButton>
  );
}

export const MutationSaveBar = Object.assign(MutationSaveBarElement, {
  Offset: '82px',
  Cancel,
  Submit,
});
